import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import ModalManager, { useModal } from '@ebay/nice-modal-react';

import Modal from 'components/UI/Modal';
import Button from 'components/UI/Button';

import styles from './styles.module.scss';

const ConfirmationModal = ModalManager.create(({
  id,
  title,
  text,
  confirmText, onConfirm,
  cancelText, onCancel, hideCancel,
  onClose,
  footer,
  confirmButtonClassName,
  cancelButtonClassName,
}) => {
  const [isConfirming, setIsConfirming] = useState(false);

  const modal = useModal(id);

  const handleCancelButtonClick = useCallback(() => {
    onCancel?.();

    modal.hide();
  }, [modal, onCancel]);

  const handleConfirmButtonClick = useCallback(async () => {
    try {
      setIsConfirming(true);
      await onConfirm?.();
    } catch (e) {
      throw e;
    } finally {
      setIsConfirming(false);
      modal.hide();
    }
  }, [modal, onConfirm]);

  const handleModalClose = useCallback(() => {
    onClose?.();

    modal.hide();
  }, [modal, onClose]);

  return (
    <Modal
      title={title}
      isOpen={modal.visible}
      onClose={handleModalClose}
      onAfterClose={modal.remove}
    >
      <p className={styles.confirmationText}>{text}</p>

      <div className={styles.buttonsContainer}>
        {!hideCancel && (
          <Button
            type="default"
            onClick={handleCancelButtonClick}
            className={cn(styles.button, cancelButtonClassName)}
            disabled={isConfirming}
          >
            {cancelText}
          </Button>
        )}

        <Button
          onClick={handleConfirmButtonClick}
          className={cn(styles.button, confirmButtonClassName)}
          loading={isConfirming}
        >
          {confirmText}
        </Button>
      </div>

      {footer}
    </Modal>
  );
});

ConfirmationModal.propTypes = {
  title: PropTypes.string,
  text: PropTypes.string,
  cancelText: PropTypes.string,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  footer: PropTypes.node,
  hideCancel: PropTypes.bool,
  confirmButtonClassName: PropTypes.string,
  cancelButtonClassName: PropTypes.string,
};

ConfirmationModal.defaultProps = {
  title: null,
  text: null,
  cancelText: 'Cancel',
  confirmText: 'Confirm',
  onConfirm: null,
  onCancel: null,
  footer: null,
  hideCancel: false,
  confirmButtonClassName: null,
  cancelButtonClassName: null,
};

export default ConfirmationModal;
